import * as OTel from '@ms/oteljs';
import { LogEventNames } from '../../interfaces/ITelemetryLogger';

/**
 * Create a new instance of OTel.TelemetryLogger, nicely hooked up with OfficeJsSink and necessary tokens.
 *
 * @returns A new instance of OTel.TelemetryLogger.
 *
 * @see OTel.TelemetryLogger
 * @see setupTelemetryLogger
 */
export function createOTelTelemetryLogger(): OTel.TelemetryLogger {
  var telemetryLogger = new OTel.TelemetryLogger();
  setupTelemetryLogger(telemetryLogger);

  return telemetryLogger;
}

function getEventFlags(): OTel.OptionalEventFlags {
  return {
    samplingPolicy: OTel.SamplingPolicy.CriticalBusinessImpact,
    dataCategories: OTel.DataCategories.ProductServicePerformance | OTel.DataCategories.ProductServiceUsage,
    diagnosticLevel: OTel.DiagnosticLevel.NecessaryServiceDataEvent
  };
}

/**
 * Create an instance of OTel.TelemetryEvent, which can be used to log the event using OTel.TelemetryLogger.sendTelemetryEvent.
 *
 * @param eventName A user defined name for event.
 * @param dataField OTel.DataField to be logged with event, which can be used to log data metrics.
 * @returns A new instance of OTel.TelemetryEvent.
 *
 * @see OTel.TelemetryEvent
 * @see OTel.DiagnosticLevel
 * @see OTel.DataCategories
 * @see OTel.DataField
 * @see OTel.TelemetryLogger.sendTelemetryEvent
 */
export function createOTelTelemetryEvent(
  eventName: string,
  dataFields: OTel.DataField[]
): OTel.TelemetryEvent {
  var telemetryEvent = {
    telemetryProperties: {
      ariaTenantToken: AriaTenantToken,
      nexusTenantToken: NexusTenantToken
    },
    eventName: 'Office.Voice.Transcription.' + eventName,
    eventFlags: getEventFlags(),
    dataFields
  };
  return telemetryEvent;
}

/**
 * Create an instance of OTel.ErrorEvent, which can be used to log the error using OTel.TelemetryLogger.sendError
 *
 * This currently doesn't use tags since our repo doesn't support a tagger yet. Once supported, ensure all calls to
 * the global LogError are switched to LogErrorTag by the tagger with a unique tag for each callsite. (VSO:4013215)
 *
 * @param errorGroup The area in which the error occured
 * @param dataFields OTel.DataField to be logged with error, which can be used to log data metrics.
 * @returns A new instance of OTel.ErrorEvent
 */
export function createOTelErrorEvent(
  errorGroup: string,
  dataFields: OTel.DataField[]
): OTel.ErrorEvent {
  return {
    eventName: 'Office.Voice.Transcription.' + LogEventNames.Error,
    error: {
      errorGroup,
      tag: 0
    },
    eventFlags: getEventFlags(),
    dataFields
  };
}

/**
 * AriaTenantToken, finalized for Office Voice Apps.
 * Every instance of OTel.TelemetryLogger has to have a AriaTenantToken, inside it's tokenTree.
 * A tokenTree defines where the events are going and what token to be use, for Application\TelemetrySink
 * identification\authentication, whenever required by logger.
 *
 * @see setupTelemetryLogger
 */
const AriaTenantToken = '9783945ebc2b468fbb8a2890cdab903b-787355a5-74c8-4a89-b06a-9c82635d75fa-7162';

/**
 * NexusTenantToken, finalized for Office Voice Apps.
 * Every instance of OTel.TelemetryLogger has to have a AriaTenantToken, inside it's tokenTree.
 * A tokenTree defines where the events are going and what token to be use, for Application\TelemetrySink
 * identification\authentication, whenever required by logger.
 *
 * @see setupTelemetryLogger
 */
const NexusTenantToken = 1763;

/**
 * A class to implement OTel.TelemetrySink interface.
 * OTel.TelemetrySink is used as telemetry sink by OTel.TelemetryLogger. To summarize,
 * OTel.TelemetrySink interface defines where the logs will be stored and maintained.
 * So below class says that they will be maintained by Office telemetry system, defined in OfficeJs.
 *
 * @see OTel.TelemetrySink
 * @see OTel.TelemetryLogger
 */
class OfficeJsSink implements OTel.TelemetrySink {
  public sendTelemetryEvent(event: OTel.TelemetryEvent) {
    Office.sendTelemetryEvent(event);
  }
}

/**
 * Internal method to setup an instance of OTel.TelemetryLogger.
 * This method connects the input instance of OTel.TelemetryLogger to OfficeJsSink
 * and set TenantTokens [required for app authentication and log writing permissions].
 *
 * @param telemetryLogger An instance of OTel.TelemetryLogger, to setup everything.
 *
 * @see OTel.TelemetryLogger
 * @see OfficeJsSink
 * @see OTel.TelemetryLogger.setTenantTokens
 */
function setupTelemetryLogger(telemetryLogger: OTel.TelemetryLogger): void {
  telemetryLogger.setTenantTokens({
    Office: {
      Voice: {
        Transcription: {
          ariaTenantToken: AriaTenantToken,
          nexusTenantToken: NexusTenantToken
        }
      }
    }
  });
  telemetryLogger.addSink(new OfficeJsSink());
}